'use client';
import { IS_CLIENT, useIsomorphicLayoutEffect } from '@repo/common-utils';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { EventTrackingController } from '../EventTrackingController';
import {
  SendEventProperties,
  SendEventOptions,
  SetUserParams,
  EventTrackingContextType,
  EventTrackingProviderProps,
  DatadogEventData,
  KakaoPixelMethods,
  KakaoPixelEventParams,
  MetaPixelEventNames,
  MetaPixelParams,
  NaverPixelEventParams
} from '../types';

import { GoogleTrackingScript, KakaoPixelScript, MetaPixelScript, TiktokPixelScript, NaverPixelScript } from './EventTrackingScripts';

export const EventTrackingContext = createContext<EventTrackingContextType>({
  eventUser: undefined,
  mixpanelDeviceId: '',
  pageName: '',
  setPageName: () => {},
  setUser: () => {},
  unsetUser: () => {},
  sendEvent: () => {},
  sendViewEvent: () => {},
  sendClickEvent: () => {},
  sendScrollEvent: () => {},
  sendRequestEvent: () => {},
  sendMetaEvent: () => {},
  sendKakaoEvent: () => {},
  sendNaverEvent: () => {},
  sendDatadogLog: () => {}
});

export const EventTrackingProvider = ({
  children,
  appId,
  clientId,
  landingId,
  isUseAuthPackages = true,
  mixpanelProps,
  airbridgeProps,
  metaPixelProps,
  naverPixelProps,
  tiktokPixelProps,
  kakaoPixelProps,
  navigationEvent,
  googleTrackingProps,
  datadogProps
}: EventTrackingProviderProps) => {
  // --- State & Refs ---
  const canInitializeMixpanel = mixpanelProps?.canInitializeMixpanel ?? true;
  const isUserUnset = useRef(true);
  const [eventUser, setEventUser] = useState<EventTrackingContextType['eventUser']>(isUseAuthPackages ? undefined : null);
  const [pageName, setPageName] = useState<string>('');
  const sentEventHistory = useRef<string[]>([]);
  const clearSentViewEventTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [eventController] = useState<EventTrackingController>(() => {
    return new EventTrackingController({
      appId,
      clientId,
      landingId,
      ...mixpanelProps,
      canInitializeMixpanel,
      ...airbridgeProps,
      ...metaPixelProps,
      ...tiktokPixelProps,
      ...naverPixelProps,
      ...googleTrackingProps,
      ...datadogProps,
      ...kakaoPixelProps
    });
  });

  const defaultProperties: Record<string, unknown> = {
    page: pageName,
    user_email: eventUser?.email,
    user_access_type: eventUser?.accessType
  };

  // --- User ---
  const setUser = (params: SetUserParams) => {
    setEventUser(params);
    eventController.setUser(params);
    isUserUnset.current = false;
  };

  const unsetUser = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if ((typeof eventUser === 'undefined' && !(searchParams.has('code') && searchParams.has('grant_type'))) || eventUser) {
      setEventUser(null);
      if (!isUserUnset.current) {
        eventController.unsetUser();
        isUserUnset.current = true;
      }
    }
  };

  // --- Event History ---
  const addSentEventHistory = useCallback((eventName: string, properties?: SendEventProperties, isAllowRetry: boolean = false): boolean => {
    const jsonData = JSON.stringify({ eventName, properties });
    const isNewEvent = !sentEventHistory.current.includes(jsonData);

    if (isNewEvent) {
      sentEventHistory.current.push(jsonData);
      if (isAllowRetry) {
        setTimeout(() => {
          const index = sentEventHistory.current.findIndex((eventString: string) => eventString === jsonData);

          if (index > -1) {
            sentEventHistory.current.splice(index, 1);
          }
        }, 2000);
      }
    }

    return isNewEvent;
  }, []);

  const clearSentEventHistory = useCallback(() => {
    if (clearSentViewEventTimer.current) {
      clearTimeout(clearSentViewEventTimer.current);
    }
    clearSentViewEventTimer.current = setTimeout(() => {
      if (!pageName) return;
      sentEventHistory.current = sentEventHistory.current.filter((eventString: string) => {
        try {
          const savedEvent = JSON.parse(eventString);
          return savedEvent.eventName?.startsWith(pageName + '_') || savedEvent.properties?.page === pageName;
        } catch {
          return false;
        }
      });
      clearSentViewEventTimer.current = null;
    }, 2000);
  }, [pageName]);

  // --- Send Events ---
  const sendEvent = (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => {
    eventController.sendEvent(eventName, { ...defaultProperties, ...properties }, options);
  };

  const sendViewEvent = (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => {
    if (!IS_CLIENT || !eventName || typeof eventUser === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('code') && searchParams.has('grant_type')) return;
    const fullEventName = `${eventName}_viewed`;
    const mergedProperties = { ...defaultProperties, ...properties };

    if (pageName && addSentEventHistory(fullEventName, mergedProperties, options?.isAllowRetry)) {
      sendEvent(fullEventName, properties, options);
    }

    if (addSentEventHistory(`pixel_${pageName}`)) {
      sendMetaEvent('PageView');
      sendKakaoEvent('pageView', pageName);
    }
  };

  const sendClickEvent = (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => {
    sendEvent(`${eventName}_clicked`, properties, options);
  };

  const sendScrollEvent = (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => {
    sendEvent(`${eventName}_scrolled`, properties, options);
  };

  const sendRequestEvent = (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => {
    sendEvent(`${eventName}_request`, properties, options);
  };

  const sendMetaEvent = (eventName: MetaPixelEventNames, params?: MetaPixelParams) => {
    eventController.sendMetaPixelEvent(eventName, params);
  };

  const sendKakaoEvent = (methodName: KakaoPixelMethods, params?: KakaoPixelEventParams) => {
    eventController.sendKakaoPixelEvent(methodName, params);
  };

  const sendNaverEvent = (params: NaverPixelEventParams) => {
    eventController.sendNaverPixelEvent(params);
  };

  const sendDatadogLog = (data: DatadogEventData) => {
    eventController.sendDatadogEvent(data);
  };

  useEffect(() => {
    if (eventController && canInitializeMixpanel) {
      eventController.setCanInitializeMixpanel(true, { utmData: mixpanelProps?.utmData, mixpanelConfig: mixpanelProps?.mixpanelConfig });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventController, canInitializeMixpanel]);

  useEffect(() => {
    navigationEvent?.registerNavigationEvent(clearSentEventHistory);
    return () => {
      navigationEvent?.removeNavigationEvent(clearSentEventHistory);
    };
  }, [navigationEvent, clearSentEventHistory]);

  useEffect(() => {
    // NOTE: PageNavigationEventContext에서 가져온 navigationEvent가 없을 경우에 수행하는 대체액션입니다.(일부 액션이 비정상적으로 동작할 수 있습니다.)
    if (navigationEvent) return;

    clearSentEventHistory();
  }, [clearSentEventHistory, navigationEvent]);

  return (
    <>
      {metaPixelProps?.metaPixelId && <MetaPixelScript />}
      {tiktokPixelProps?.tiktokPixelId && <TiktokPixelScript />}
      {naverPixelProps?.naverPixelId && <NaverPixelScript />}
      {googleTrackingProps && <GoogleTrackingScript {...googleTrackingProps} />}
      {kakaoPixelProps && <KakaoPixelScript />}
      <EventTrackingContext.Provider
        value={{
          eventUser,
          mixpanelDeviceId: eventController.getMixpanelDeviceId(),
          pageName,
          setPageName,
          setUser,
          unsetUser,
          sendEvent,
          sendViewEvent,
          sendClickEvent,
          sendScrollEvent,
          sendRequestEvent,
          sendMetaEvent,
          sendKakaoEvent,
          sendNaverEvent,
          sendDatadogLog
        }}
      >
        {children}
      </EventTrackingContext.Provider>
    </>
  );
};

export const useUserEventLogger = (currentPageName?: string) => {
  const context = useContext(EventTrackingContext);

  useIsomorphicLayoutEffect(() => {
    if (currentPageName && currentPageName !== context.pageName) context.setPageName(currentPageName);
  }, [currentPageName, context.pageName, context.setPageName]);

  return context;
};
